import React, { useId } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { T, useLang } from "../i18n";
import { isPackageTransport } from "../utils/packageTransport";

// Schritt 5, nur Artefaktweg: Der Workflow gleicht ein bestehendes Zielpaket an die Paketinfos der Quelle an. Die Vorprüfung
// liefert je Ziel `paketInfoAbgleich: { felder: [{ feld, quelle, ziel }], fehler? }` mit den abweichenden Feldern.
// Reiner Hinweis ohne Schalter und ohne Sperre; fehlt das Feld (älterer Server) oder ist die Erweiterung älter als v26,
// erscheint nichts.

const MAX_CHARS = 160;

/** Paketangabe als Klartext: SAP liefert die Beschreibung als HTML (`<p>…</p>`), Absätze werden zu Leerzeichen. */
export function packageInfoText(value) {
  if (value == null) return "";
  const html = String(value).replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ").replace(/<\/(p|div|li|h[1-6])>/gi, " ");
  const text = typeof DOMParser === "function"
    ? new DOMParser().parseFromString(html, "text/html").body.textContent || ""
    : html.replace(/<[^>]*>/g, " ");
  return text.replace(/\s+/g, " ").trim();
}

const fieldsOf = entry => {
  const felder = entry?.paketInfoAbgleich?.felder;
  return Array.isArray(felder) ? felder.filter(item => item && typeof item.feld === "string" && item.feld) : [];
};

function Value({ value }) {
  const { t } = useLang();
  const text = packageInfoText(value);
  if (!text) return <Box component="span" sx={{ color: "var(--iio-text-3)" }}>{t("pkgInfo.empty")}</Box>;
  if (text.length <= MAX_CHARS) return <span>{text}</span>;
  return <Tooltip title={text} describeChild>
    <Box component="span" tabIndex={0} sx={{ "&:focus-visible": { outline: "2px solid var(--iio-signal)", outlineOffset: 2 } }}>
      {`${text.slice(0, MAX_CHARS).trimEnd()}…`}
    </Box>
  </Tooltip>;
}

function FieldRow({ item }) {
  const { t } = useLang();
  const key = `pkgInfo.field.${item.feld}`;
  return <Box component="li" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "10rem minmax(0, 1fr)" }, columnGap: 2, rowGap: 0.25,
    py: 0.75, borderTop: "1px solid var(--iio-border-subtle)", overflowWrap: "anywhere" }}>
    <Box component="strong" sx={{ fontWeight: 600, color: "var(--iio-text-1)" }}>{T[key] ? t(key) : item.feld}</Box>
    <Box component="span" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1, rowGap: 0.25, minWidth: 0 }}>
      <Box component="span" sx={{ color: "var(--iio-text-2)", minWidth: 0 }}>{t("pkgInfo.before")}: <Value value={item.ziel} /></Box>
      <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: 14, color: "var(--iio-text-3)", alignSelf: "center" }} />
      <Box component="span" sx={{ color: "var(--iio-text-1)", minWidth: 0 }}>{t("pkgInfo.after")}: <Value value={item.quelle} /></Box>
    </Box>
  </Box>;
}

function TargetGroup({ entry, felder, labelled, failed }) {
  const { t } = useLang();
  const labelId = useId();
  const packageName = entry.targetPackageName || entry.targetPackageId;
  const content = <>
    {felder.length > 0 && <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {felder.map(item => <FieldRow key={item.feld} item={item} />)}
    </Box>}
    {failed && <Typography component="p" sx={{ m: 0, mt: felder.length ? 0.75 : 0, fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-2)" }}>
      {t("pkgInfo.readFailed")}
    </Typography>}
  </>;
  if (!labelled) return content;
  return <Box role="group" aria-labelledby={labelId} sx={{ "& + &": { mt: 1.5 } }}>
    <Box id={labelId} sx={{ fontSize: 13, fontWeight: 600, color: "var(--iio-text-2)", mb: 0.5, overflowWrap: "anywhere" }}>
      {entry.instance?.name || entry.instance?.id || ""}
      {entry.werk?.kennung && <> · {entry.werk.name ? `${entry.werk.name} ` : ""}<span className="ken">{entry.werk.kennung}</span></>}
      {packageName && <> · {packageName}</>}
    </Box>
    {content}
  </Box>;
}

// Erst ab dieser Version übernimmt der Workflow die Paketinfos auch in ein bestehendes Zielpaket; davor nur beim Anlegen.
export const PACKAGE_INFO_MIN_EXTENSION = 26;

function alignmentGroups(state) {
  if (isPackageTransport(state)) return [];
  return Object.entries(state?.precheckByBucket || {})
    .filter(([, entry]) => entry && !entry.error && Array.isArray(entry.comparison) && entry.transportMode !== "package")
    .map(([key, entry]) => ({ key, entry, felder: fieldsOf(entry), failed: !!entry.paketInfoAbgleich?.fehler }))
    .filter(group => group.felder.length || group.failed);
}

/** Gibt es überhaupt etwas zu zeigen? Erst dann lohnt es, die Version der Erweiterung zu lesen. */
export const hasPackageInfoAlignment = state => alignmentGroups(state).length > 0;

export default function PackageInfoAlignment({ state, extensionVersion }) {
  const { t } = useLang();
  const headingId = useId();
  // Unbekannte oder ältere Erweiterung: Der Satz „werden übernommen“ stimmte dort nicht, also nichts zeigen.
  if (typeof extensionVersion !== "number" || extensionVersion < PACKAGE_INFO_MIN_EXTENSION) return null;
  const groups = alignmentGroups(state);
  if (!groups.length) return null;
  const withFields = groups.some(group => group.felder.length);
  const labelled = Object.keys(state?.precheckByBucket || {}).length > 1;
  return <Box component={withFields ? "section" : "div"} className="sect" aria-labelledby={withFields ? headingId : undefined}
    sx={{ borderLeft: "2px solid var(--iio-border)", pl: 1.5, py: 0.5, fontSize: 13, color: "var(--iio-text-1)" }}>
    {withFields && <>
      <Typography id={headingId} component="h3" sx={{ m: 0, fontFamily: "inherit", fontSize: 15, fontWeight: 600 }}>{t("pkgInfo.title")}</Typography>
      <Typography component="p" sx={{ m: 0, mb: 0.75, fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-2)" }}>{t("pkgInfo.hint")}</Typography>
    </>}
    {groups.map(group => <TargetGroup key={group.key} entry={group.entry} felder={group.felder} failed={group.failed} labelled={labelled} />)}
  </Box>;
}
